/*
  SimulateSection.jsx
*/

// Npm.
import React, { useState, useEffect, useMemo } from 'react';

// Lib.
import cleanWhiteSpace from '../../../utils/cleanWhiteSpace';
import graphDataToBotech from '../../../utils/graphDataToBotech';
import getBotechErrorMessage from '../../../utils/getBotechErrorMessage';
import getApiUrl from '../../../utils/getApiUrl';
import sanitizeFileName from '../../../utils/sanitizeFileName';

// Components.
import BottomActions from './BottomActions';
import Button from '../../Button';
import Grid from '../../Grid';
import TextField from '../../TextField';
import TextFieldOnBlur from '../../TextFieldOnBlur';
import Selector from '../../Selector';

// Hooks.
import useThrottle from '../../../hooks/useThrottle';
import useSetPromise from '../../../hooks/useSetPromise';
import useBlobUrl from '../../../hooks/useBlobUrl';
import useBotech from '../../../hooks/useBotech';

// Makes.
import { makeStyles } from '../../../makes/makeStyles';

// Constants.
const MIN_YEAR = 0;
const MAX_YEAR = 2201;
const ENVIRONMENT_OPTIONS = [
  {
    label: 'Default',
    value: 'default'
  },
  {
    label: 'Standard',
    value: 'standard'
  },
  {
    label: 'Appendix 3',
    value: 'appendix_3'
  },
  {
    label: 'Palestine',
    value: 'palestine'
  }
];

// Helper to get a downloadable file name,
// for when we are downloading from local CSV data.
function getResultName(name) {

  // Name provided. Sanitize.
  name = name && sanitizeFileName(name, 'csv');
  if (name) {
    return name;
  }

  // Default name of "botech_results_YYY-MM-DD-HHMMSS.csv".
  const date = new Date();
  return 'botech_results_'
    + String(date.getFullYear()).padStart(3, '0') + '-'
    + String(date.getMonth() + 1).padStart(1, '0') + '-'
    + String(date.getDate()).padStart(1, '0') + '-'
    + String(date.getHours()).padStart(1, '0')
    + String(date.getMinutes()).padStart(1, '0')
    + String(date.getSeconds()).padStart(1, '0')
    + '.csv';
}

// Styles.
const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.space(6),
  },
  title: {
    color: theme.color_primary_main,
    marginBottom: theme.space(2),
  },
  statusText: {
    color: theme.color_text_secondary,
    marginRight: theme.space(1),
    wordBreak: 'break-word',
  },
}));

// Cache for the current promise.
let _cachedPromise = null;

// Hacky place to store the last state of the form fields.
// TODO: Move this somewhere else later.
const _lastState = {
  fileId: '',
  userId: '',
};

/**
 * Section for simulating the model.
 */
export default function SimulateSection({
  className,
  graphData,
  onChangeGraphData,
  disabled = false,
  ...rest
}) {

  const classes = useStyles();
  const botech = useBotech();

  const metadata = graphData._botech?.metadata || {};
  const runtime = graphData._botech?.runtime || {};

  // Handlers.
  // TODO: These will cause undo/redo events. Is this desired?
  // Probably skip this with a second argument, "skipHistory".

  function changeRuntime(fields) {
    if (onChangeGraphData && fields) {
      onChangeGraphData(obj => {
        const next = { ...obj };
        next._botech = { ...next._botech };
        next._botech.runtime = { ...next._botech.runtime, ...fields };
        return next;
      });
    }
  }

  function changeMetadata(fields) {
    if (onChangeGraphData && fields) {
      onChangeGraphData(obj => {
        const next = { ...obj };
        next._botech = { ...next._botech };
        next._botech.metadata = { ...next._botech.metadata, ...fields };
        return next;
      });
    }
  }

  // State that we hackily control with "_lastState" so we don't
  // lose previous values for the session.

  const [userId, _setUserId] = useState(_lastState.userId || botech.userId || 'default');
  const [fileId, _setFileId] = useState(_lastState.fileId || '');

  function setUserId(value) {
    _lastState.userId = value;
    _setUserId(value);
  }

  function setFileId(value) {
    _lastState.fileId = value;
    _setFileId(value);
  }

  // Local state.

  const [statusText, setStatusText] = useState('');
  const [resultName, setResultName] = useState('');
  const [resultUrl, setResultUrl] = useState('');
  const [summaryUrl, setSummaryUrl] = useState('');
  const [resultBlob, setResultBlob] = useState(null);

  // Get URLs for the result buttons.

  const resultBlobUrl = useBlobUrl(resultBlob);
  let useResultUrl = '';
  if (resultBlobUrl) {
    useResultUrl = resultBlobUrl;
  } else if (resultUrl) {
    useResultUrl = getApiUrl(botech.apiUrl, resultUrl);
  }

  // Optional form component that sits at the bottom.

  const ExtraFormComponent = botech.runModelExtraFormComponent;
  const [extraForm, setExtraForm] = useState(null);

  function handleChangeExtraForm(value) {
    setExtraForm(value);
  }

  // Promise result handler.

  const [promise, setPromise] = useSetPromise((err, res) => {
    if (err) {
      
      setStatusText(err.message);

    } else {

      // Optional results name, just for the downloaded file.
      let filename = '';
      if (res.csv_filename) {
        filename = res.csv_filename;
      } else if (metadata.name) {
        filename = `${metadata.name} results`;
      }
      setResultName(getResultName(filename));

      // Check for csv_data (quicker in-memory download), or csv_url (S3 download).
      if (res.csv_data) {
        setResultBlob(new Blob([res.csv_data], { type: 'text/csv' }));
      } else if (res.csv_url) {
        setResultUrl(res.csv_url);
      }

      // Summary URL.
      if (res.summary_url) {
        setSummaryUrl(res.summary_url);
      }

      // Existing file_id, or generated file_id if running a new result.
      if (res.file_id) {
        setFileId(res.file_id);
      }

    }
  });

  const handleClickRun = useThrottle(() => {
    
    // Reset state.
    
    setResultBlob(null);
    setResultUrl('');
    setSummaryUrl('');
    setResultName('');

    // Convert and validate data.

    const data = graphDataToBotech(graphData);
    const errorMessage = getBotechErrorMessage(data);
    if (errorMessage) {
      setStatusText(errorMessage);
      return;
    }

    // Call API.

    try {
      if (botech.runModel) {

        // If "botech.runModel" is provided, use this in favour of the older "makeApiCall",
        // and with parameters that don't need to be exact according to the current API.

        _cachedPromise = botech.runModel({
          model_data: data,
          file_id: fileId || null,
          environment_label: userId || null,
          extra_form: extraForm || null,
        });

      } else {

        // Standalone API call.
        // https://api.forecasthealth.org/docs#/Running%20Models/run_model_run__environment_label__post
        
        const body = { data, store: true };

        if (fileId) {
          body.file_id = fileId;
        }

        _cachedPromise = botech.makeApiCall('POST', `/run/${encodeURIComponent(userId)}`, body).promise;

      }
      setPromise(_cachedPromise);
      setStatusText('');
    } catch (err) {
      setStatusText(err);
    }

  }, 500);

  useEffect(() => {
    if (_cachedPromise) {
      setPromise(_cachedPromise);
    }
  }, [setPromise]);

  function handleSubmit(event) {
    event.preventDefault();
    handleClickRun();
  }

  // Common props.

  const useDisabled = disabled || promise != null;
  const titleClass = `${classes.title} overline paragraph`;

  return (
    <div className={className}>
      <div className={classes.section}>

        <p className={titleClass}>
          Project Info
        </p>

        <Grid container spacing={2}>
          <Grid xs={12} sm={8}>

            <TextFieldOnBlur
              label="Author"
              value={metadata.author || ''}
              onChange={(value) => changeMetadata({ author: cleanWhiteSpace(value) })}
              fullWidth
              disabled={useDisabled}
              maxLength="128"
            />

          </Grid>
          <Grid xs={12} sm={4}>

            <Selector
              label="Botech Version"
              value={metadata.version || ''}
              onChange={(event) => changeMetadata({ version: event.target.value })}
              fullWidth
              disabled={useDisabled}
            >
              <option value="" disabled>-- select --</option>
              <option value="1.0">v1.0</option>
            </Selector>

          </Grid>
          <Grid xs={12}>

            <TextFieldOnBlur
              label="Title"
              value={metadata.name || ''}
              onChange={(value) => changeMetadata({ name: cleanWhiteSpace(value) })}
              fullWidth
              disabled={useDisabled}
              maxLength="128"
            />

          </Grid>
          <Grid xs={12}>

            <TextFieldOnBlur
              label="Description"
              value={metadata.description || ''}
              onChange={(value) => changeMetadata({ description: value.trim() })}
              fullWidth
              disabled={useDisabled}
              maxLength="2048"
            />

          </Grid>
        </Grid>

      </div>
      <form onSubmit={handleSubmit}>
        <div className={classes.section}>

          <p className={titleClass}>
            Simulation Parameters
          </p>

          <Grid container spacing={2}>
            <Grid xs={12} sm={6}>

              <TextFieldOnBlur
                label="Start Year"
                value={typeof runtime.startYear === 'number' ? String(runtime.startYear) : ''}
                onChange={(value) => changeRuntime({ startYear: (value && Number(value)) || 0 })}
                fullWidth
                required
                disabled={useDisabled}
                placeholder={`${MIN_YEAR} - ${MAX_YEAR}`}
                type="number"
                min={MIN_YEAR}
                max={MAX_YEAR}
              />

            </Grid>
            <Grid xs={12} sm={6}>

              <TextFieldOnBlur
                label="End Year"
                value={typeof runtime.endYear === 'number' ? String(runtime.endYear) : ''}
                onChange={(value) => changeRuntime({ endYear: (value && Number(value)) || 0 })}
                fullWidth
                required
                disabled={useDisabled}
                placeholder={`${MIN_YEAR} - ${MAX_YEAR}`}
                type="number"
                min={MIN_YEAR}
                max={MAX_YEAR}
              />

            </Grid>
            <Grid xs={12} sm={6}>

              <Selector
                label="Environment"
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
                fullWidth
                required
                disabled={useDisabled}
              >
                <option value="" disabled>-- select --</option>
                {
                  ENVIRONMENT_OPTIONS.map(opt =>
                    <option key={opt.value} value={opt.value}>{ opt.label }</option>
                  )
                }
              </Selector>

            </Grid>
            <Grid xs={12} sm={6}>

              <TextField
                label="File ID"
                value={fileId}
                placeholder="Optional ID"
                onChange={(event) => setFileId(event.target.value)}
                fullWidth
                disabled={useDisabled}
                maxLength="128"
              />

            </Grid>
            {
              Boolean(ExtraFormComponent) &&
              <Grid xs={12}>
                <ExtraFormComponent
                  disabled={useDisabled}
                  value={extraForm}
                  onChange={handleChangeExtraForm}
                />
              </Grid>
            }
          </Grid>

        </div>
        <BottomActions>
          {
            Boolean(statusText) &&
            <span className={`${classes.statusText} body2`}>
              { statusText }
            </span>
          }
          {
            Boolean(summaryUrl) &&
            <Button
              component="a"
              target="_blank"
              href={getApiUrl(botech.apiUrl, summaryUrl)}
            >
              View Summary
            </Button>
          }
          {
            Boolean(useResultUrl) &&
            <Button
              component="a"
              target="_blank"
              href={useResultUrl}
              download={resultName}
            >
              Download Result
            </Button>
          }
          <Button type="submit" disabled={useDisabled}>
            { promise != null ? 'Running' : 'Run Simulation' }
          </Button>
        </BottomActions>
      </form>
    </div>
  );
}
